import PricingCTA from '@/components/blocks/PricingCTA.vue';
import CommunityView from '@/components/community/CommunityView.vue';

export async function getConfig() {
  return {
    env: {
      isMobileApp: process.env.MOBILE_APP || false,
    },

    core: {
      // core.app.js поднимает wsManager.initialize(...) после гидратации;
      // приложения/сторы потом только дёргают connect(userId)+subscribeModule+addEventListener.
      websocket: true,
    },

    app: {
      // Канонический origin СТРАНИЦ сайта (для canonical/og:url/alternate в martyrs App.vue).
      // Литерал, НЕ process.env: getConfig() выполняется и на клиенте, где process.env не инлайнится.
      // Это НЕ файловый сервер/CDN (тот — FILE_SERVER_URL, префикс ассетов).
      url: 'https://traffers.ru',
      support: {
        url: 'https://t.me/traffers_support'
      },
      head: {
        // Метрика-инициализация теперь делает martyrs/analytics client-adapter
        // (см. modules.analytics ниже). Тут оставляем только GA4 и noscript-маяк.
        script: [
          { src: 'https://www.googletagmanager.com/gtag/js?id=G-4SGGQ59GNY', async: true },
          {
            children: `window.dataLayer = window.dataLayer || [];
function gtag(){dataLayer.push(arguments);}
gtag('js', new Date());
gtag('config', 'G-4SGGQ59GNY');`
          },
          // Site-wide JSON-LD: Organization. Сущность бренда для rich-результатов
          // и AI-экстракции (GEO).
          {
            type: 'application/ld+json',
            children: JSON.stringify({
              '@context': 'https://schema.org',
              '@type': 'Organization',
              name: 'Traffers',
              url: 'https://traffers.ru',
              logo: 'https://traffers.ru/favicon/cover.jpg',
              description: 'Платформа автоматизации продвижения в Telegram: парсинг аудитории, инвайты, просмотры историй, прогрев аккаунтов через прокси.',
              sameAs: ['https://t.me/traffers_support'],
            }),
          },
        ],
        // Верификация вебмастеров — env-driven (токены не в гите). Появляются в
        // <head> на всех роутах (главная обязательна для Яндекса) только когда
        // заданы SEO_GOOGLE_VERIFICATION / SEO_YANDEX_VERIFICATION. Токены
        // публичные (по дизайну в HTML) — секретности не требуют.
        meta: [
          ...(process.env.SEO_GOOGLE_VERIFICATION
            ? [{ name: 'google-site-verification', content: process.env.SEO_GOOGLE_VERIFICATION }]
            : []),
          ...(process.env.SEO_YANDEX_VERIFICATION
            ? [{ name: 'yandex-verification', content: process.env.SEO_YANDEX_VERIFICATION }]
            : []),
        ],
        noscript: [
          { children: '<div><img src="https://mc.yandex.ru/watch/108725943" style="position:absolute; left:-9999px;" alt="" /></div>' }
        ]
      },
      messages: {
        ru: {
          meta: {
            title: 'Traffers — Автоматизация Telegram-аккаунтов',
            description: 'Платформа для массовых операций в Telegram: парсинг участников, инвайты, просмотры историй, masslurking, прогрев аккаунтов через прокси.',
            keywords: 'telegram, автоматизация, парсинг, инвайты, masslurking, прокси, telegram-аккаунты, traffers',
            og: {
              title: 'Traffers — Автоматизация Telegram-аккаунтов',
              description: 'Парсинг, инвайты, просмотры историй, masslurking и прогрев Telegram-аккаунтов через прокси — из одного интерфейса.',
            },
            twitter: {
              title: 'Traffers — Автоматизация Telegram-аккаунтов',
              description: 'Парсинг, инвайты, просмотры историй, masslurking и прогрев Telegram-аккаунтов через прокси — из одного интерфейса.',
              card: 'summary_large_image'
            },
            htmlAttrs: {
              lang: 'ru'
            }
          },
        }
      }
    },

    modules: {
      currency: '₽',
      // Auth подключаем на КАЖДЫЙ роут (routes: ['/']) и preload — нужен store
      // для Header (колокольчик нотификаций) и SSR-инициализация по cookie `user`
      // в core.app.js работает только если store.auth уже зарегистрирован.
      // contexts: ['backoffice'] — НЕ регистрируем клиентские users-роуты
      // (`/users/:_id`, `/users/:_id/edit/*`, `/users/:_id/dashboard`); собственный
      // профиль Traffers живёт по `/dashboard/profile`. Backoffice users
      // (`/backoffice/users/*`) остаются доступны для админ-контекста.
      auth: {
        loader: () => import('@ozdao/martyrs/modules/auth/client'),
        routes: ['/'],
        priority: 'high',
        preload: true,
        // 'client' включает users-роутер martyrs: /users/:_id (Profile), /users/:_id/edit/*,
        //   /users/:_id/dashboard — на них завязан сайдбар. 'backoffice' — админ-страницы users.
        contexts: ['client', 'backoffice'],
        dependencies: [],
        // Регистрация/вход только по email: SignIn/SignUp/ResetPassword martyrs
        // читают core.state.options.auth.authMethodsExclude и прячут таб «Телефон»
        // (SMS-провайдер не подключён).
        authMethodsExclude: ['phone'],
      },
      // Notifications нужны на dashboard (колокольчик в шапке + страница профиля
      // с привязкой Telegram). Preload, чтобы store был готов сразу при входе.
      // settings.routeUsers: 'Dashboard' — страница «все уведомления» монтируется
      // под /dashboard/notifications. Дефолтный родитель модуля — 'User Profile Root'
      // (/users/:_id/notifications). ВАЖНО: именно settings — module.manager.register()
      // режет произвольные top-level ключи по whitelist'у, passthrough только у settings.
      notifications: {
        loader: () => import('@ozdao/martyrs/modules/notifications/client'),
        routes: ['/dashboard'],
        priority: 'normal',
        preload: true,
        dependencies: ['auth'],
        settings: { routeUsers: 'Dashboard' },
      },
      // Payments preload на /dashboard — нужен store.subscriptions с current
      // подпиской/лимитами для guard'ов в Tasks/Accounts/Proxies/CreateTaskPopup.
      payments: {
        loader: () => import('@ozdao/martyrs/modules/payments/client'),
        routes: ['/dashboard'],
        priority: 'high',
        preload: true,
        dependencies: ['auth'],
      },
      // Analytics — first-class модуль аналитики. Все track-вызовы летят на наш бэк
      // (POST /api/analytics/track), бэк по адаптерам выгружает во внешние трекеры.
      // Клиентский metrika-adapter здесь нужен только чтобы Метрика поставила _ym_uid
      // cookie и трекала автоматические pageview/webvisor — без этого server-side MP
      // не сможет привязать конверсию к юзеру.
      analytics: {
        loader: () => import('@ozdao/martyrs/modules/analytics/client'),
        routes: ['/'],
        priority: 'high',
        preload: true,
        dependencies: [],
        settings: {
          adapters: [
            { name: 'metrika', counterId: 108725943, webvisor: true, clickmap: true, trackLinks: true, accurateTrackBounce: true, ecommerce: 'dataLayer' },
          ],
        },
      },
      // Backoffice — клиент-only админ-дашборд. Гард `requiresAdmin` сидит внутри
      // самого модуля (в `backoffice.router.js`), маршрут `/backoffice/*`.
      backoffice: {
        loader: () => import(
          /* webpackChunkName: "module-backoffice" */
          '@ozdao/martyrs/modules/backoffice/client'
        ),
        routes: ['/backoffice'],
        priority: 'normal',
        dependencies: ['auth'],
      },
      // Community — блоги. Контекст 'organization' пропускаем сознательно
      // (модуль организаций НЕ подключён); routes управляются ТОЛЬКО `contexts`,
      // в роутерные файлы community не лезем.
      // Настройка `posting` уходит и в клиентский store (см. community.client.js),
      // и в серверный ABAC (см. server.js → community.initialize(... posting ...)).
      community: {
        loader: () => import(
          /* webpackChunkName: "module-community" */
          '@ozdao/martyrs/modules/community/client'
        ),
        routes: ['/blog', '/posts', '/community', '/backoffice'],
        priority: 'normal',
        dependencies: ['auth'],
        contexts: ['client', 'user', 'backoffice'],
        posting: 'admin',           // 'public' | 'auth' | 'admin'
        // Проект РАСШИРЯЕТ блоки конструктора: community зарегистрирует их в
        // blockManager при своей инициализации (см. community.client.js). Компонент
        // живёт в проекте; в статью вставляется блоком { type: 'PricingCTA', content }.
        settings: {
          blocks: { PricingCTA },
          // Проектный layout 3-колоночного shell community: оборачивает
          // martyrs Community.vue и наполняет колонки контентом Traffers
          // (hero, темы-кластеры, бесплатные инструменты, CTA). См.
          // src/components/community/ и community.client.js (options.settings.layout).
          layout: CommunityView,
        },
      },
    },
  };
}
